import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, CircleNotch, WhatsappLogo, XCircle } from "@phosphor-icons/react";
import { api, whatsappLink } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useLang();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | failed
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setInfo(data);
        if (data.payment_status === "paid") return setStatus("paid");
        if (["failed", "expired"].includes(data.payment_status)) return setStatus("failed");
      } catch (e) { /* keep polling */ }
      attempts += 1;
      if (attempts > 12) return setStatus("failed");
      setTimeout(poll, 2000);
    };
    poll();
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center px-5 bg-background relative overflow-hidden">
      <div className="blob w-[400px] h-[400px] -top-20 -left-20" style={{ background: "hsl(351 100% 82%)" }} />
      <div className="blob w-[350px] h-[350px] bottom-0 right-0" style={{ background: "hsl(200 80% 82%)" }} />
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="relative rounded-[2.5rem] bg-white shadow-soft-lg border border-white p-10 max-w-md w-full text-center"
      >
        <img src="/logo.png" alt="logo" className="h-14 mx-auto mb-6" />
        {status === "checking" && (
          <>
            <CircleNotch className="animate-spin mx-auto text-primary" size={56} />
            <h1 className="font-display font-bold text-2xl mt-5 text-foreground">Confirmando tu pago...</h1>
            <p className="text-foreground/55 font-medium mt-2">Un momento por favor.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200, damping: 12 }}>
              <CheckCircle weight="fill" className="mx-auto text-green-500" size={72} />
            </motion.div>
            <h1 className="font-display font-bold text-3xl mt-5 text-foreground">{t("success.title")}</h1>
            <p className="text-foreground/60 font-medium mt-3">
              {t("success.thanks")} {info?.package_name ? `(${info.package_name})` : ""}. {t("success.willStart")}
            </p>
            {info?.tracking_code && (
              <div className="mt-5 rounded-2xl bg-muted p-4">
                <p className="text-xs font-bold text-foreground/40 uppercase tracking-wider">{t("success.yourCode")}</p>
                <p data-testid="success-tracking-code" className="font-display font-bold text-3xl text-foreground tracking-widest">{info.tracking_code}</p>
                <button
                  data-testid="success-track-link"
                  onClick={() => navigate("/seguimiento")}
                  className="mt-2 text-sm font-bold text-primary hover:underline"
                >
                  {t("success.trackLink")}
                </button>
              </div>
            )}
            <a
              data-testid="success-whatsapp"
              href={whatsappLink("¡Hola! Acabo de realizar mi pedido de una canción 🎵. Quiero dar seguimiento.")}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-bold text-white"
              style={{ background: "#25D366" }}
            >
              <WhatsappLogo weight="fill" size={20} /> {t("success.wa")}
            </a>
          </>
        )}
        {status === "failed" && (
          <>
            <XCircle weight="fill" className="mx-auto text-destructive" size={64} />
            <h1 className="font-display font-bold text-2xl mt-5 text-foreground">{t("success.failTitle")}</h1>
            <p className="text-foreground/55 font-medium mt-2">{t("success.failDesc")}</p>
          </>
        )}
        <button
          data-testid="success-home"
          onClick={() => navigate("/")}
          className="mt-6 block mx-auto text-sm font-bold text-foreground/50 hover:text-primary"
        >
          {t("success.home")}
        </button>
      </motion.div>
    </div>
  );
}
